import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { AlertCircle } from 'lucide-react';

/**
 * Input Component
 * 
 * Form input with validation states
 * 
 * @param {string} label - Input label
 * @param {string} error - Error message
 * @param {string} helperText - Helper text below input
 * @param {ReactComponent} icon - Left icon
 * @param {ReactComponent} rightIcon - Right icon
 * @param {string} className - Additional CSS classes
 */
const Input = forwardRef(({
  label,
  error,
  helperText,
  icon: Icon,
  rightIcon: RightIcon,
  className = '',
  fullWidth = true,
  ...props
}, ref) => {
  const inputClasses = clsx(
    'w-full px-4 py-3 rounded-xl border transition-all duration-200',
    'focus:outline-none focus:ring-2',
    Icon && 'pl-11',
    RightIcon && 'pr-11',
    error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
      : 'border-gray-200 focus:border-brand-red focus:ring-brand-red/20',
    'disabled:bg-gray-50 disabled:cursor-not-allowed',
    props.type === 'textarea' && 'resize-none',
    className
  );

  const containerClasses = clsx(
    fullWidth ? 'w-full' : ''
  );

  const InputComponent = props.type === 'textarea' ? 'textarea' : 'input';

  return (
    <div className={containerClasses}>
      {label && (
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
          {props.required && <span className="text-brand-red ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {Icon && (
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
            <Icon className="w-5 h-5 text-gray-400" />
          </div>
        )}
        
        <InputComponent
          ref={ref}
          className={inputClasses}
          {...props}
        />
        
        {RightIcon && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            <RightIcon className="w-5 h-5 text-gray-400" />
          </div>
        )}
        
        {error && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            <AlertCircle className="w-5 h-5 text-red-500" />
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
          {error}
        </p>
      )}
      
      {helperText && !error && (
        <p className="mt-2 text-sm text-gray-500">
          {helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
